// @ts-ignore
import React, { type ReactNode } from 'react';
import '../CyberModal.css';

interface CyberModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProceed?: () => void;
  title?: string;
  subtitle?: string;
  children: ReactNode;
  proceedLabel?: string;
  cancelLabel?: string;
  carouselImages?: string[]; // 🆕 optional carousel images
}

export const CyberModal: React.FC<CyberModalProps> = ({
                                                        isOpen,
                                                        onClose,
                                                        onProceed,
                                                        title = "Cyber Modal",
                                                        subtitle = "Welcome to the Cyberpunk World",
                                                        children,
                                                        proceedLabel = "Proceed",
                                                        cancelLabel = "Sluiten",
                                                        carouselImages = [],
                                                      }) => {
  const [glitchActive, setGlitchActive] = React.useState(false);
  const [currentSlide, setCurrentSlide] = React.useState(0);
  const [fullscreenIndex, setFullscreenIndex] = React.useState<number | null>(null);
  const modalBodyRef = React.useRef<HTMLElement>(null);

  React.useEffect(() => {
    if (!isOpen) return;

    const handleKeyPress = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        if (fullscreenIndex !== null) {
          setFullscreenIndex(null);
        } else {
          onClose();
        }
      } else if (e.key === 'Enter') {
        onProceed?.();
      } else if (fullscreenIndex !== null && carouselImages.length > 1) {
        if (e.key === 'ArrowRight') {
          setFullscreenIndex((prev) => (prev! + 1) % carouselImages.length);
        } else if (e.key === 'ArrowLeft') {
          setFullscreenIndex((prev) => prev === 0 ? carouselImages.length - 1 : prev! - 1);
        }
      }
    };

    // Prevent background scrolling by fixing the body in place
    const scrollY = window.scrollY;
    document.body.style.position = 'fixed';
    document.body.style.top = `-${scrollY}px`;
    document.body.style.left = '0';
    document.body.style.right = '0';
    document.body.style.overflow = 'hidden';
    document.documentElement.style.overflow = 'hidden';

    // Intercept all wheel events and redirect them to the modal body
    const handleWheel = (e: WheelEvent) => {
      e.preventDefault();
      e.stopPropagation();
      if (modalBodyRef.current) {
        modalBodyRef.current.scrollTop += e.deltaY;
      }
    };

    const glitchTimer = setTimeout(() => {
      setGlitchActive(true);
      setTimeout(() => setGlitchActive(false), 2000);
    }, 1500);

    window.addEventListener('keydown', handleKeyPress);
    window.addEventListener('wheel', handleWheel, { passive: false });
    return () => {
      // Restore body position and scroll
      document.body.style.position = '';
      document.body.style.top = '';
      document.body.style.left = '';
      document.body.style.right = '';
      document.body.style.overflow = '';
      document.documentElement.style.overflow = '';
      window.scrollTo(0, scrollY);
      window.removeEventListener('keydown', handleKeyPress);
      window.removeEventListener('wheel', handleWheel);
      clearTimeout(glitchTimer);
    };
  }, [isOpen, onClose, onProceed, fullscreenIndex, carouselImages.length]);

  const nextSlide = () => {
    setCurrentSlide((prev) => (prev + 1) % carouselImages.length);
  };

  const prevSlide = () => {
    setCurrentSlide((prev) =>
        prev === 0 ? carouselImages.length - 1 : prev - 1
    );
  };

  if (!isOpen) return null;

  return (
      <>
      <div className="cyber-modal-overlay" onClick={onClose}>
        <div className="cyber-modal" onClick={(e) => e.stopPropagation()}>
          <section className="cyber-modal__body" ref={modalBodyRef}>
            <div className="body__backdrop">
              <div className="backdrop">
                <div className="version">v001.e1349837856</div>
                <div className="corner"></div>
              </div>
            </div>
            <div className="body__content">
              <h2>
                <span>{title}</span>
                <br />
                <span className="subtitle">{subtitle}</span>
              </h2>

              <div className="body__text">{children}</div>

              <div className={`modal__glitch ${glitchActive ? 'active' : ''}`}>
                <h2>
                  <span>{title}</span>
                </h2>
                <div className="body__text">{children}</div>
              </div>
              {carouselImages.length > 0 && (
                  <div className="cyber-carousel">
                    <button className="carousel-btn left" onClick={prevSlide}>
                      ‹
                    </button>
                    <div className="carousel-track">
                      {carouselImages.map((src, index) => (
                          <img
                              key={index}
                              src={src}
                              alt={`slide-${index}`}
                              className={`carousel-image ${
                                  index === currentSlide ? 'active' : ''
                              }`}
                              onClick={() => index === currentSlide && setFullscreenIndex(index)}
                              style={{ cursor: index === currentSlide ? 'pointer' : 'default', pointerEvents: index === currentSlide ? 'auto' : 'none' }}
                          />
                      ))}
                    </div>
                    <button className="carousel-btn right" onClick={nextSlide}>
                      ›
                    </button>
                  </div>
              )}
            </div>
          </section>
          <div className="cyber-modal__actions">
            <button aria-label="Cancel" className="cyber-btn" onClick={onClose}>
            <span className="backdrop">
              <span className="corner"></span>
            </span>
              <kbd>esc</kbd>
              <span>{cancelLabel}</span>
            </button>
          </div>
        </div>
      </div>

      {fullscreenIndex !== null && (
        <div className="fullscreen-overlay" onClick={() => setFullscreenIndex(null)}>
          {carouselImages.length > 1 && (
            <button
              className="fullscreen-nav left"
              onClick={(e) => {
                e.stopPropagation();
                setFullscreenIndex((prev) => prev === 0 ? carouselImages.length - 1 : prev! - 1);
              }}
            >
              ‹
            </button>
          )}
          <img src={carouselImages[fullscreenIndex]} alt="Fullscreen" className="fullscreen-image" onClick={(e) => e.stopPropagation()} />
          {carouselImages.length > 1 && (
            <button
              className="fullscreen-nav right"
              onClick={(e) => {
                e.stopPropagation();
                setFullscreenIndex((prev) => (prev! + 1) % carouselImages.length);
              }}
            >
              ›
            </button>
          )}
          <button className="fullscreen-close" onClick={() => setFullscreenIndex(null)}>×</button>
        </div>
      )}
      </>
  );
};

interface CyberButtonProps {
  onClick: () => void;
  label?: string;
  shortcut?: string;
}

// Trigger button component
export const CyberButton: React.FC<CyberButtonProps> = ({onClick, label = "Open", shortcut = "O",}) => {
  return (
      <button className="cyber-trigger-btn" onClick={onClick}>
      <span className="backdrop">
        <span className="corner"></span>
      </span>
        <kbd>{shortcut}</kbd>
        <span>{label}</span>
      </button>
  );
};
